/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * A text field or select for the canvas inspector: the label sits above a
 * 36px input instead of floating in its border, which the base theme draws
 * cramped in a narrow panel. Takes TextField's props.
 */

import { useId } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { v2 as t } from "styles/tokens/v2";

export const inputSx = {
  "& .MuiInputBase-root": { minHeight: 36, borderRadius: "8px", bgcolor: t.surface, fontSize: 14, color: t.text },
  "& .MuiInputBase-input": { py: "7px", px: 1.25, fontSize: 14 },
  "& .MuiInputBase-inputMultiline": { p: 0 },
  "& .MuiInputBase-multiline": { py: "8px", px: 1.25 },
  "& .MuiSelect-select": { py: "7px", minHeight: "unset", display: "flex", alignItems: "center" },
  "& .MuiSelect-icon": { right: 6, color: t.text3 },
  "& fieldset": { borderColor: t.border2, top: 0, "& legend": { display: "none" } },
  "& .MuiInputBase-root:hover fieldset": { borderColor: t.text3 },
  "& .MuiInputBase-root.Mui-focused fieldset": { borderColor: t.primary, borderWidth: "1.5px" },
  "& .MuiInputBase-root.Mui-disabled": { bgcolor: t.surface2 },
  "& .MuiFormHelperText-root": { mx: 0, mt: 0.5, fontSize: 12.5 },
};

export const labelSx = { display: "block", fontSize: 13, fontWeight: 600, color: t.text2, mb: 0.5 };

export default function LabeledField({ label, required, sx, inputProps, SelectProps, children, ...props }) {
  const id = useId();
  // A select's visible box is a div the <label> cannot point at.
  const selectProps = props.select ? { ...SelectProps, SelectDisplayProps: { "aria-label": typeof label === "string" ? label : undefined, ...SelectProps?.SelectDisplayProps } } : SelectProps;
  return (
    <Box sx={{ minWidth: 0, ...sx }}>
      <Typography component="label" htmlFor={id} sx={labelSx}>
        {label}
        {required && (
          <Box component="span" aria-hidden="true" sx={{ color: "#B42318", ml: 0.25 }}>
            *
          </Box>
        )}
      </Typography>
      <TextField id={id} fullWidth required={required} inputProps={{ ...inputProps }} SelectProps={selectProps} sx={inputSx} {...props}>
        {children}
      </TextField>
    </Box>
  );
}

LabeledField.propTypes = {
  label: PropTypes.node.isRequired,
  required: PropTypes.bool,
  sx: PropTypes.object,
  inputProps: PropTypes.object,
  SelectProps: PropTypes.object,
  select: PropTypes.bool,
  children: PropTypes.node,
};
LabeledField.defaultProps = { required: false, sx: {}, inputProps: {}, SelectProps: undefined, select: false, children: null };
